/**
 * MechanicalTableDropdown - Dropdown for switching between mechanical sub-tables
 * 
 * Used within DisciplineMatrix to allow users to switch between:
 * - Rotating Equipment Tracking
 * - Vessels Tracking
 * - (Future: Pumps, Compressors, etc.)
 * 
 * Each sub-table has its own:
 * - Template
 * - Column configuration (UDFs)
 * - Rules of Credit
 */

import React, { useState, useRef, useEffect } from 'react';

/**
 * Available mechanical sub-tables
 * Each entry maps to a separate discipline config and backend routes
 */
export const MECHANICAL_SUB_TABLES = [
  {
    id: 'rotating-equipment',
    label: 'Rotating Equipment',
    disciplineId: 'mechanical',
    tableName: 'rotating_equipment_tracking',
    rocTable: 'rules_of_credit_mechanical',
  },
  {
    id: 'vessels',
    label: 'Vessels',
    disciplineId: 'vessels',
    tableName: 'vessels_tracking',
    rocTable: 'rules_of_credit_vessels',
  },
  {
    id: 'chutes',
    label: 'Chutes',
    disciplineId: 'chutes',
    tableName: 'chutes_tracking',
    rocTable: 'rules_of_credit_chutes',
  },
  {
    id: 'conveyors',
    label: 'Conveyors',
    disciplineId: 'conveyors',
    tableName: 'conveyors_tracking',
    rocTable: 'rules_of_credit_conveyors',
  },
  {
    id: 'skids',
    label: 'Skids',
    disciplineId: 'skids',
    tableName: 'skids_tracking',
    rocTable: 'rules_of_credit_skids',
  },
  // Future sub-tables:
  // { id: 'pumps', label: 'Pumps', disciplineId: 'pumps', tableName: 'pumps_tracking', rocTable: 'rules_of_credit_pumps' },
  // { id: 'compressors', label: 'Compressors', disciplineId: 'compressors', tableName: 'compressors_tracking', rocTable: 'rules_of_credit_compressors' },
];

/**
 * Get sub-table config by ID
 */
export const getSubTableById = (subTableId) => {
  return MECHANICAL_SUB_TABLES.find(t => t.id === subTableId) || MECHANICAL_SUB_TABLES[0];
};

/**
 * MechanicalTableDropdown Component
 * 
 * @param {string} currentSubTableId - Currently selected sub-table ID
 * @param {function} onSubTableChange - Callback when sub-table is changed
 * @param {boolean} disabled - Whether the dropdown is disabled
 */
const MechanicalTableDropdown = ({ currentSubTableId, onSubTableChange, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentTable = getSubTableById(currentSubTableId);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = (subTableId) => {
    if (subTableId !== currentSubTableId) {
      onSubTableChange(subTableId);
    }
    setIsOpen(false);
  };

  const dropdownStyle = {
    position: 'relative',
    display: 'inline-block',
  };

  const buttonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: disabled ? '#e9ecef' : '#fff',
    border: '1px solid #ced4da',
    borderRadius: '6px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    color: disabled ? '#6c757d' : '#212529',
    minWidth: '180px',
    justifyContent: 'space-between',
    transition: 'all 0.2s ease',
  };

  const menuStyle = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: '4px',
    backgroundColor: '#fff',
    border: '1px solid #ced4da',
    borderRadius: '6px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    zIndex: 1000,
    overflow: 'hidden',
  };

  const menuItemStyle = (isSelected) => ({
    padding: '10px 12px',
    cursor: 'pointer',
    backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
    borderLeft: isSelected ? '3px solid #1976d2' : '3px solid transparent',
    fontSize: '14px',
    color: isSelected ? '#1565c0' : '#212529',
    fontWeight: isSelected ? '500' : '400',
    transition: 'all 0.15s ease',
  });

  const labelStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: '#6c757d',
    marginRight: '12px',
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {/* Label */}
      <div style={labelStyle}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <path d="M4 6h16v2H4zm0 5h16v2H4zm0 5h16v2H4z"/>
        </svg>
        <span>Table:</span>
      </div>

      {/* Dropdown */}
      <div ref={dropdownRef} style={dropdownStyle}>
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          style={buttonStyle}
          disabled={disabled}
          onMouseEnter={(e) => {
            if (!disabled) {
              e.currentTarget.style.borderColor = '#80bdff';
              e.currentTarget.style.boxShadow = '0 0 0 2px rgba(0, 123, 255, 0.1)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#ced4da';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <span>{currentTable.label}</span>
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="currentColor"
            style={{
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          >
            <path d="M7 10l5 5 5-5z" />
          </svg>
        </button>

        {isOpen && (
          <div style={menuStyle}>
            {MECHANICAL_SUB_TABLES.map((table) => (
              <div
                key={table.id}
                onClick={() => handleSelect(table.id)}
                style={menuItemStyle(table.id === currentSubTableId)}
                onMouseEnter={(e) => {
                  if (table.id !== currentSubTableId) {
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                  }
                }}
                onMouseLeave={(e) => {
                  if (table.id !== currentSubTableId) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {table.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MechanicalTableDropdown;
